import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, ArrowUpDown, ArrowDown, ArrowUp, AlertTriangle, CheckCircle2, Clock, FileText, ChevronDown, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loan, LoanStage, Scope, STAGE_LABELS, STAGE_COLORS, filterLoansByStageAndScope, formatCurrency, formatDate, getEventDate } from '../data/loans';
interface LoanFilesPanelProps {
  open: boolean;
  loans: Loan[];
  initialStage: LoanStage | null;
  initialScope: Scope;
  initialSpeedBonusOnly?: boolean;
  onClose: () => void;
}
type SortKey = 'date' | 'amount' | 'borrower' | 'daysInStage' | 'speedBonus';
type SortDir = 'asc' | 'desc';
const LOAN_TYPES = ['Conventional', 'FHA', 'VA', 'Jumbo'] as const;
export function LoanFilesPanel({
  open,
  loans,
  initialStage,
  initialScope,
  initialSpeedBonusOnly = false,
  onClose
}: LoanFilesPanelProps) {
  const LOANS = loans;
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState<LoanStage | 'all'>(initialStage ?? 'all');
  const [scope, setScope] = useState<Scope>(initialScope);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [movingFilter, setMovingFilter] = useState<'all' | 'moving' | 'stuck'>('all');
  const [speedBonusOnly, setSpeedBonusOnly] = useState(initialSpeedBonusOnly);
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  // Reset filters when reopened
  useEffect(() => {
    if (open) {
      setStage(initialStage ?? 'all');
      setScope(initialScope);
      setSpeedBonusOnly(initialSpeedBonusOnly);
      setSearch('');
      setTypeFilter([]);
      setMovingFilter('all');
      setSortKey(initialSpeedBonusOnly ? 'speedBonus' : 'date');
      setSortDir('desc');
    }
  }, [open, initialStage, initialScope, initialSpeedBonusOnly]);
  // Close on escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);
  const filteredLoans = useMemo(() => {
    let result: Loan[] = LOANS;
    // Speed bonus filter — scope means closing date in that window
    if (speedBonusOnly) {
      result = result.filter(l => l.speedBonus && l.speedBonus > 0);
      result = result.filter(l => {
        const closing = l.events.find(e => e.stage === 'closing');
        if (!closing) return false;
        if (scope === 'today') return closing.date === '2025-05-14';
        if (scope === 'mtd') return closing.date >= '2025-05-01' && closing.date <= '2025-05-14';
        return true;
      });
    } else if (stage !== 'all') {
      result = filterLoansByStageAndScope(result, stage, scope);
    } else if (scope !== 'all') {
      result = result.filter(loan => loan.events.some(e => {
        if (scope === 'today') return e.date === '2025-05-14';
        if (scope === 'mtd') return e.date >= '2025-05-01' && e.date <= '2025-05-14';
        return true;
      }));
    }
    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(l => l.borrower.toLowerCase().includes(q) || l.property.toLowerCase().includes(q) || l.id.toLowerCase().includes(q));
    }
    // Type filter
    if (typeFilter.length > 0) {
      result = result.filter(l => typeFilter.includes(l.loanType));
    }
    // Moving filter
    if (movingFilter === 'moving') result = result.filter(l => l.isMoving);
    if (movingFilter === 'stuck') result = result.filter(l => !l.isMoving);
    // Sort
    const sorted = [...result].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'date') {
        const aDate = stage !== 'all' ? getEventDate(a, stage as LoanStage) : a.events[a.events.length - 1].date;
        const bDate = stage !== 'all' ? getEventDate(b, stage as LoanStage) : b.events[b.events.length - 1].date;
        cmp = (aDate ?? '').localeCompare(bDate ?? '');
      } else if (sortKey === 'amount') {
        cmp = a.loanAmount - b.loanAmount;
      } else if (sortKey === 'borrower') {
        cmp = a.borrower.localeCompare(b.borrower);
      } else if (sortKey === 'daysInStage') {
        cmp = a.daysInCurrentStage - b.daysInCurrentStage;
      } else if (sortKey === 'speedBonus') {
        cmp = (a.speedBonus ?? 0) - (b.speedBonus ?? 0);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [loans, stage, scope, search, typeFilter, movingFilter, speedBonusOnly, sortKey, sortDir]);
  const totalPoints = useMemo(() => {
    if (speedBonusOnly) {
      return filteredLoans.reduce((s, l) => s + (l.speedBonus ?? 0), 0);
    }
    return filteredLoans.reduce((sum, l) => {
      if (stage !== 'all') {
        const e = l.events.find(ev => ev.stage === stage);
        return e ? sum + e.points : sum;
      }
      return sum + l.events.reduce((s, e) => s + e.points, 0);
    }, 0);
  }, [filteredLoans, stage, speedBonusOnly]);
  const totalVolume = filteredLoans.reduce((s, l) => s + l.loanAmount, 0);
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };
  const toggleType = (type: string) => {
    setTypeFilter(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };
  const clearFilters = () => {
    setSearch('');
    setStage('all');
    setTypeFilter([]);
    setMovingFilter('all');
    setSpeedBonusOnly(false);
  };
  const headerTitle = speedBonusOnly ? 'Speed Bonus Loans' : stage !== 'all' ? `${STAGE_LABELS[stage as LoanStage]}s` : 'All Loan Files';
  const headerEyebrow = speedBonusOnly ? `${scope === 'today' ? 'Today' : scope === 'mtd' ? 'Month to Date' : 'All Time'} · Fast Closes` : `${scope === 'today' ? 'Today' : scope === 'mtd' ? 'Month to Date' : 'All Time'} · Drill-down`;
  return <AnimatePresence data-id="element-262">
      {open && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/40 z-40" onClick={onClose} data-id="element-263" />

          {/* Panel */}
          <motion.aside initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 280
      }} className="fixed top-0 right-0 bottom-0 w-full md:w-[860px] bg-gray-50 z-50 shadow-2xl flex flex-col" role="dialog" aria-label="Loan files drill-down" data-id="element-264">
            {/* Header */}
            <div className={`${speedBonusOnly ? 'bg-orange-500' : 'bg-[#0a1e5e]'} text-white px-6 py-4 flex items-center justify-between shrink-0 transition-colors`} data-id="element-265">
              <div className="flex items-center gap-3 min-w-0" data-id="element-266">
                {speedBonusOnly && <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center shrink-0" data-id="element-267">
                    <Zap className="w-5 h-5" data-id="element-268" />
                  </div>}
                <div className="min-w-0" data-id="element-269">
                  <p className={`text-xs font-semibold uppercase tracking-wider ${speedBonusOnly ? 'text-orange-100' : 'text-blue-200'}`} data-id="element-270">
                    {headerEyebrow}
                  </p>
                  <h2 className="text-2xl font-extrabold tracking-tight truncate" data-id="element-271">
                    {headerTitle}
                  </h2>
                </div>
              </div>
              <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors shrink-0" aria-label="Close panel" data-id="element-272">
                <X className="w-5 h-5" data-id="element-273" />
              </button>
            </div>

            {/* Summary Strip */}
            <div className="bg-white border-b border-gray-200 px-6 py-3 grid grid-cols-3 gap-4 shrink-0" data-id="element-274">
              <div data-id="element-275">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-276">
                  {speedBonusOnly ? 'Fast Closes' : 'Files'}
                </p>
                <p className="text-2xl font-extrabold text-[#0a1e5e]" data-id="element-277">
                  {filteredLoans.length}
                </p>
              </div>
              <div data-id="element-278">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-279">
                  Total Volume
                </p>
                <p className="text-2xl font-extrabold text-[#0a1e5e]" data-id="element-280">
                  {formatCurrency(totalVolume)}
                </p>
              </div>
              <div data-id="element-281">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-282">
                  {speedBonusOnly ? 'Bonus Points' : 'Points'}
                </p>
                <p className={`text-2xl font-extrabold ${speedBonusOnly ? 'text-orange-500' : 'text-green-600'}`} data-id="element-283">
                  +{totalPoints.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Speed Bonus explainer (when active) */}
            {speedBonusOnly && <div className="bg-orange-50 border-b border-orange-200 px-6 py-3 text-xs text-orange-900 flex items-start gap-2 shrink-0" data-id="element-284">
                <Zap className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" data-id="element-285" />
                <p data-id="element-286">
                  <strong data-id="element-287">Speed Bonus rules:</strong> Doc Out → Close within{' '}
                  <strong data-id="element-288">14 days = +1,500 pts</strong>, within{' '}
                  <strong data-id="element-289">21 days = +500 pts</strong>.
                </p>
              </div>}

            {/* Filters */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0" data-id="element-290">
              <div className="flex flex-col gap-3" data-id="element-291">
                <div className="flex flex-col md:flex-row gap-3" data-id="element-292">
                  <div className="relative flex-1" data-id="element-293">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-294" />
                    <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search borrower, property, or loan ID..." className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-295" />
                  </div>
                  <div className="flex bg-gray-100 rounded-lg p-1" data-id="element-296">
                    {(['today', 'mtd', 'all'] as Scope[]).map(s => <button key={s} onClick={() => setScope(s)} className={`px-3 py-1 text-xs font-bold uppercase rounded-md transition-colors ${scope === s ? 'bg-white text-[#0a1e5e] shadow-sm' : 'text-gray-600 hover:text-[#0a1e5e]'}`} data-id="element-297">
                        {s === 'today' ? 'Today' : s === 'mtd' ? 'MTD' : 'All'}
                      </button>)}
                  </div>
                </div>

                {/* Speed Bonus toggle */}
                <div className="flex flex-wrap gap-2 items-center" data-id="element-298">
                  <button onClick={() => setSpeedBonusOnly(!speedBonusOnly)} className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full border transition-colors ${speedBonusOnly ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:text-orange-600'}`} data-id="element-299">
                    <Zap className="w-3.5 h-3.5" data-id="element-300" />
                    Speed Bonus Only
                  </button>
                </div>

                {/* Stage Pills */}
                <div className="flex flex-wrap gap-2 items-center" data-id="element-301">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mr-1" data-id="element-302">
                    Stage:
                  </span>
                  <button onClick={() => setStage('all')} disabled={speedBonusOnly} className={`px-3 py-1 text-xs font-bold rounded-full border transition-colors ${speedBonusOnly ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' : stage === 'all' ? 'bg-[#0a1e5e] text-white border-[#0a1e5e]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`} data-id="element-303">
                    All
                  </button>
                  {(Object.keys(STAGE_LABELS) as LoanStage[]).map(s => {
                const c = STAGE_COLORS[s];
                const active = stage === s;
                return <button key={s} onClick={() => setStage(s)} disabled={speedBonusOnly} className={`px-3 py-1 text-xs font-bold rounded-full border transition-colors ${speedBonusOnly ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' : active ? `${c.bg} ${c.text} ${c.border}` : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`} data-id="element-304">
                        {STAGE_LABELS[s]}
                      </button>;
              })}
                </div>

                {/* Type + Moving */}
                <div className="flex flex-wrap gap-2 items-center" data-id="element-305">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mr-1" data-id="element-306">
                    Type:
                  </span>
                  {LOAN_TYPES.map(t => {
                const active = typeFilter.includes(t);
                return <button key={t} onClick={() => toggleType(t)} className={`px-2.5 py-1 text-xs font-semibold rounded border transition-colors ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`} data-id="element-307">
                        {t}
                      </button>;
              })}
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider ml-3 mr-1" data-id="element-308">
                    Status:
                  </span>
                  {(['all', 'moving', 'stuck'] as const).map(m => <button key={m} onClick={() => setMovingFilter(m)} className={`px-2.5 py-1 text-xs font-semibold rounded border transition-colors ${movingFilter === m ? m === 'stuck' ? 'bg-red-100 text-red-700 border-red-200' : m === 'moving' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-[#0a1e5e] text-white border-[#0a1e5e]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`} data-id="element-309">
                      {m === 'all' ? 'All' : m === 'moving' ? 'Moving' : 'Not Moving'}
                    </button>)}
                  {(search || stage !== 'all' || typeFilter.length > 0 || movingFilter !== 'all' || speedBonusOnly) && <button onClick={clearFilters} className="ml-auto text-xs font-bold text-blue-600 hover:text-blue-800 underline" data-id="element-310">
                      Clear filters
                    </button>}
                </div>
              </div>
            </div>

            {/* Sort Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-2 shrink-0 hidden md:grid grid-cols-12 gap-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-311">
              <button onClick={() => toggleSort('borrower')} className="col-span-4 text-left flex items-center gap-1 hover:text-[#0a1e5e]" data-id="element-312">
                Borrower / Property
                <SortIcon active={sortKey === 'borrower'} dir={sortDir} data-id="element-313" />
              </button>
              <div className="col-span-2" data-id="element-314">
                {speedBonusOnly ? 'Bonus' : 'Stage'}
              </div>
              <button onClick={() => toggleSort('amount')} className="col-span-2 text-right flex items-center gap-1 justify-end hover:text-[#0a1e5e]" data-id="element-315">
                Amount
                <SortIcon active={sortKey === 'amount'} dir={sortDir} data-id="element-316" />
              </button>
              <button onClick={() => toggleSort('date')} className="col-span-2 text-left flex items-center gap-1 hover:text-[#0a1e5e]" data-id="element-317">
                {speedBonusOnly ? 'Close Date' : 'Stage Date'}
                <SortIcon active={sortKey === 'date'} dir={sortDir} data-id="element-318" />
              </button>
              <button onClick={() => toggleSort(speedBonusOnly ? 'speedBonus' : 'daysInStage')} className="col-span-2 text-right flex items-center gap-1 justify-end hover:text-[#0a1e5e]" data-id="element-319">
                {speedBonusOnly ? 'Speed' : 'Days'}
                <SortIcon active={sortKey === (speedBonusOnly ? 'speedBonus' : 'daysInStage')} dir={sortDir} data-id="element-320" />
              </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto px-4 md:px-6 py-3" data-id="element-321">
              {filteredLoans.length === 0 ? <div className="text-center py-16" data-id="element-322">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-323" />
                  <p className="text-gray-500 font-semibold" data-id="element-324">
                    {speedBonusOnly ? 'No fast closes in this timeframe' : 'No loan files match these filters'}
                  </p>
                  <button onClick={clearFilters} className="mt-3 text-sm text-blue-600 font-bold hover:underline" data-id="element-325">
                    Clear filters
                  </button>
                </div> : <ul className="space-y-2" data-id="element-326">
                  {filteredLoans.map(loan => <LoanRow key={loan.id} loan={loan} contextStage={stage !== 'all' && !speedBonusOnly ? stage as LoanStage : undefined} speedBonusMode={speedBonusOnly} data-id="element-327" />)}
                </ul>}
            </div>
          </motion.aside>
        </>}
    </AnimatePresence>;
}
function SortIcon({
  active,
  dir
}: {
  active: boolean;
  dir: SortDir;
}) {
  if (!active) return <ArrowUpDown className="w-3 h-3 opacity-40" data-id="element-328" />;
  return dir === 'asc' ? <ArrowUp className="w-3 h-3" data-id="element-329" /> : <ArrowDown className="w-3 h-3" data-id="element-330" />;
}
function LoanRow({
  loan,
  contextStage,
  speedBonusMode
}: {
  loan: Loan;
  contextStage?: LoanStage;
  speedBonusMode?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const c = STAGE_COLORS[loan.currentStage];
  // Compute days from doc out → close for speed bonus context
  const docOut = loan.events.find(e => e.stage === 'doc_out');
  const closing = loan.events.find(e => e.stage === 'closing');
  const docToCloseDays = docOut && closing ? Math.round((new Date(closing.date).getTime() - new Date(docOut.date).getTime()) / 86400000) : null;
  const displayDate = speedBonusMode ? closing?.date : contextStage ? getEventDate(loan, contextStage) : loan.events[loan.events.length - 1].date;
  return <li className={`bg-white border ${expanded ? 'border-blue-300 shadow-md' : 'border-gray-200'} rounded-lg transition-all`} data-id="element-331">
      <button onClick={() => setExpanded(!expanded)} className="w-full text-left p-3 md:grid md:grid-cols-12 md:gap-3 md:items-center flex flex-col gap-2 hover:bg-gray-50/50 rounded-lg" data-id="element-332">
        {/* Borrower */}
        <div className="col-span-4 min-w-0" data-id="element-333">
          <p className="font-bold text-[#0a1e5e] text-sm truncate" data-id="element-334">
            {loan.borrower}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-335">{loan.property}</p>
          <p className="text-[10px] text-gray-400 font-mono" data-id="element-336">
            {loan.id} · {loan.loanType}
          </p>
        </div>

        {/* Bonus pill OR Stage pill */}
        <div className="col-span-2" data-id="element-337">
          {speedBonusMode ? <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold bg-orange-100 text-orange-700" data-id="element-338">
              <Zap className="w-3 h-3" data-id="element-339" />+{loan.speedBonus} pts
            </span> : <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold ${c.bg} ${c.text}`} data-id="element-340">
              {loan.isMoving ? <CheckCircle2 className="w-3 h-3" data-id="element-341" /> : <AlertTriangle className="w-3 h-3" data-id="element-342" />}
              {STAGE_LABELS[loan.currentStage]}
            </span>}
        </div>

        {/* Amount */}
        <div className="col-span-2 md:text-right" data-id="element-343">
          <p className="text-sm font-bold text-[#0a1e5e]" data-id="element-344">
            {formatCurrency(loan.loanAmount)}
          </p>
        </div>

        {/* Date */}
        <div className="col-span-2" data-id="element-345">
          <p className="text-xs text-gray-600" data-id="element-346">
            {displayDate ? formatDate(displayDate) : '—'}
          </p>
        </div>

        {/* Days / Speed */}
        <div className="col-span-2 md:text-right flex md:block items-center justify-between" data-id="element-347">
          {speedBonusMode && docToCloseDays !== null ? <p className="text-sm font-bold text-orange-600" data-id="element-348">
              {docToCloseDays}d
              <span className="text-[10px] text-gray-400 font-medium ml-1" data-id="element-349">
                doc → close
              </span>
            </p> : <p className={`text-sm font-bold ${loan.daysInCurrentStage > 5 && !loan.isMoving ? 'text-red-600' : 'text-gray-700'}`} data-id="element-350">
              {loan.daysInCurrentStage}d
              <span className="text-[10px] text-gray-400 font-medium ml-1" data-id="element-351">
                in stage
              </span>
            </p>}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} data-id="element-352" />
        </div>
      </button>

      {expanded && <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: 'auto',
      opacity: 1
    }} className="border-t border-gray-100 px-4 py-3 bg-gray-50/50 overflow-hidden" data-id="element-353">
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-354">
            Stage Timeline
          </p>
          <ol className="relative space-y-2 pl-4 border-l-2 border-gray-200" data-id="element-355">
            {loan.events.map(ev => {
          const ec = STAGE_COLORS[ev.stage];
          return <li key={ev.stage} className="relative" data-id="element-356">
                  <span className={`absolute -left-[19px] top-1.5 w-3 h-3 rounded-full ${ec.bg} ${ec.text} border-2 border-white`} data-id="element-357"></span>
                  <div className="flex items-center justify-between" data-id="element-358">
                    <div data-id="element-359">
                      <p className="text-xs font-bold text-[#0a1e5e]" data-id="element-360">
                        {STAGE_LABELS[ev.stage]}
                      </p>
                      <p className="text-[10px] text-gray-500" data-id="element-361">
                        {formatDate(ev.date)}
                      </p>
                    </div>
                    <span className={`text-xs font-bold ${ec.text}`} data-id="element-362">
                      +{ev.points} pts
                    </span>
                  </div>
                </li>;
        })}
          </ol>
          {loan.speedBonus ? <div className="mt-3 bg-orange-50 border border-orange-200 rounded-lg p-2.5 flex items-center gap-2" data-id="element-363">
              <Zap className="w-4 h-4 text-orange-500 shrink-0" data-id="element-364" />
              <p className="text-xs font-semibold text-orange-700" data-id="element-365">
                Speed Bonus: <strong data-id="element-366">+{loan.speedBonus} pts</strong>
                {docToCloseDays !== null && <span className="text-orange-600/80" data-id="element-367">
                    {' '}
                    · Doc Out to Close in {docToCloseDays} days
                  </span>}
              </p>
            </div> : null}
          {!loan.isMoving && <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2 flex items-center gap-2" data-id="element-368">
              <AlertTriangle className="w-4 h-4 text-red-500" data-id="element-369" />
              <p className="text-xs font-semibold text-red-700" data-id="element-370">
                File not moving — losing 50 pts/day until status changes
              </p>
            </div>}
        </motion.div>}
    </li>;
}